import { useEffect, useRef, forwardRef } from "react";
import { useTranslation } from "next-i18next";
import PropTypes from "prop-types";
import styles from "./styles/reviewSection.module.css";
import { Button } from "@components/Button";
import {
  useReviews,
  fetchMoreReviews,
  getInitialReviews,
  sortReviews,
  REVIEWS_ACTIONS,
} from "./hooks/useReviews";
import { Review } from "./Review";
import { Summary } from "./Summary";
import { Sort } from "./Sort";
import { useMobileDetect } from "@hooks/index";
import { Spinner } from "@components/Spinner";

export const ReviewsSection = forwardRef(
  ({ shopifyProductID, staticReviews, staticProduct, showReviews, closeReviewsDrawer }, ref) => {
    const { state, dispatch } = useReviews(staticProduct, staticReviews);
    const { t } = useTranslation("reviews");
    const { isLoading, junipReviews, junipProduct, page, sortBy } = state;
    const currentDevice = useMobileDetect();
    const isMobile = currentDevice.isMobile();
    const containerRef = useRef(null);

    useEffect(() => {
      const handleGetReviews = async () => {
        await getInitialReviews(dispatch, shopifyProductID);
      };
      if (shopifyProductID) {
        dispatch({ type: REVIEWS_ACTIONS.RESET });
        handleGetReviews();
      }
    }, [shopifyProductID]);

    async function loadMoreReviews(e) {
      e.preventDefault();
      const container = containerRef.current;
      const currentScrollTop = container.scrollTop;

      await fetchMoreReviews(dispatch, junipProduct.id, page.after, sortBy);

      container.scrollTop = currentScrollTop + 400;
    }

    function handleSort(value) {
      sortReviews(dispatch, junipProduct.id, value);
    }

    if (!junipReviews.length || !junipProduct) {
      return null;
    }

    const sizeRatings = junipProduct?.active_traits
      .filter((trait) => trait.name === "Fit")
      .map((trait) => trait.rating_average);

    return (
      <div className={styles.reviewsContainer}>
        <Summary
          junipProduct={junipProduct}
          handleCloseDrawer={closeReviewsDrawer}
          sizeRatings={sizeRatings}
        />
        {/* {isMobile && (
          <div className={styles.reviewButtonContainer}>
            <button className={styles["review-button"]}>{t("Write a Review")}</button>
          </div>
        )} */}
        <Sort handleSort={handleSort} sortBy={sortBy} />
        <article className={styles.container} ref={ref}>
          <ul className={styles.list} ref={containerRef}>
            {junipReviews.map((review) => (
              <Review review={review} key={review.id} />
            ))}
          </ul>
          {page.after && (
            <div className={styles.button}>
              <Button
                variant="secondary"
                handleClick={loadMoreReviews}
                isLoading={isLoading}
                description="moreReviews"
                descriptionText="Fetch more customer reviews"
                full
              >
                {isLoading ? <Spinner /> : t("more-reviews-button")}
              </Button>
            </div>
          )}
        </article>
      </div>
    );
  }
);

ReviewsSection.propTypes = {
  shopifyProductID: PropTypes.number.isRequired,
  staticReviews: PropTypes.object,
  staticProduct: PropTypes.object,
  showReviews: PropTypes.bool.isRequired,
  closeReviewsDrawer: PropTypes.func.isRequired,
};
